import { PLATFORM } from "aurelia-pal";
import { autoinject, bindable } from "aurelia-framework";
import { GridOptions } from "ag-grid";
import { HttpResponseMessage } from "aurelia-http-client";
import { I18N } from "aurelia-i18n";
import { Datasource, DatasourceFactory } from "../../services/datasource";
import { User } from "./user";
import { AuthService } from "../../services/auth/auth-service";
import { DialogFactory } from "../../services/dialog-factory";

@autoinject
export class Users {
  @bindable() errorMessage?: string;

  gridOptions: GridOptions = {
    enableServerSideSorting: true,
    enableServerSideFilter: true,
    enableColResize: true,
    rowModelType: "infinite",
    paginationPageSize: 100,
    context: {
      // remember this view model, for later use in callbacks
      viewModel: this,
      editUser: this.editUser,
      unlockUser: this.unlockUser,
      deleteUser: this.deleteUser,
    },
  };

  private datasource: Datasource;

  constructor(
    datasourceFactory: DatasourceFactory,
    private dialogFactory: DialogFactory,
    private authService: AuthService,
    private i18n: I18N,
  ) {
    this.datasource = datasourceFactory.createDatasource("admin/user");
    this.gridOptions.datasource =
      this.datasource.createGridDatasourceServerSideFiltering(undefined, [
        { colId: "logon_id", sort: "asc" },
      ]);
  }

  onGridSizeChanged() {
    this.gridOptions.api!.sizeColumnsToFit();
  }

  newUser() {
    let user = new User();
    user.role = "gast";
    user.enabled = true;
    user.$status = "inserted";
    this.openEditUserDialog(user, "new");
  }

  /** note: this is called from an ag-grid callback */
  editUser(user: User) {
    let self = (<any>this).viewModel; // hack to access "this" from an ag-grid callback
    self.openEditUserDialog(user, "edit");
  }

  /** note: this is called from an ag-grid callback */
  unlockUser(user: User) {
    let self = (<any>this).viewModel; // hack to access "this" from an ag-grid callback
    self.askReallyUnlock(user);
  }

  /** note: this is called from an ag-grid callback */
  deleteUser(user: User) {
    let self = (<any>this).viewModel; // hack to access "this" from an ag-grid callback
    self.askReallyDelete(user);
  }

  private openEditUserDialog(user: User, mode: "edit" | "new") {
    this.dialogFactory
      .open(PLATFORM.moduleName("views/admin/edit-user"), {
        user: user,
        mode: mode,
      })
      .then((r) => {
        if (r.wasCancelled) return;
        let user: User = r.output;
        if (user.$status == "unchanged") user.$status = "changed";
        if (user.newPassword)
          user.newPassword = this.authService.hash(user.newPassword);
        this.save(user);
      });
  }

  askReallyUnlock(user: User) {
    let message = this.i18n.tr("admin.users.action.reallyUnlock", {
      username: user.logon_id,
    });
    this.dialogFactory.openOkCancel(message).then((confirmed) => {
      if (confirmed) {
        user.enabled = true;
        user.$status = "changed";
        this.save(user);
      }
    });
  }

  askReallyDelete(user: User) {
    if (user.logon_id == "admin") {
      let message = this.i18n.tr("admin.users.action.notAllowedToDelete", {
        username: user.logon_id,
      });
      this.dialogFactory.openError(message);
    } else {
      let message = this.i18n.tr("admin.users.action.reallyDelete", {
        username: user.logon_id,
      });
      this.dialogFactory.openOkCancel(message).then((confirmed) => {
        if (confirmed) {
          user.$status = "deleted";
          this.save(user);
        }
      });
    }
  }

  private save(user: User) {
    this.datasource
      .update([user])
      .then(() => {
        this.reload();
      })
      .catch((e: HttpResponseMessage) => {
        this.errorMessage =
          "unexpected error: " +
          e.statusCode +
          " " +
          e.statusText +
          (e.response ? ": " + e.response : "");
      });
  }

  private reload() {
    this.gridOptions.api!.purgeInfiniteCache();
    this.errorMessage = undefined;
  }
}
